import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useBoolean,
  useOutsideClick,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BsArrowRight } from 'react-icons/bs';
import { FiCalendar } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import moment from 'moment';

import Calendar from 'components/Icons/Calendar';
import PlanPermission from 'container/PlanPermission';
import DatePickerInputButton from 'pages/ModelOverviewPage/DatePickerWithButton';
import TimeScalePermission from 'pages/PermissionComponent/TimeScalePermission';
import { getTimeStatus, resetTimeStatus } from 'redux/ModelsSlice';
import { TimeProperties } from 'models/response/model.interface';
import {
  fetchAnalysisSectionDataVersion2,
  getDimension,
  getModelDetails,
  getSelectedBlockDimension,
  modelDetailStatus,
  overallIndicatorFilter,
  setPlanPageDate,
  overallGroupByFilter,
} from 'redux/PlanPageSlice';
import {
  fetchCompareAnalysisSectionData,
  fetchCompareTableData,
  getBaseScenario,
  getCompareScenario,
} from 'redux/ScenarioSlice';
import { getUserSubscriptionLimits } from 'redux/UserSlice';
import { getPlanPageThemeColors } from 'redux/ThemeSlice';
import { PlanToolbarChevronDownIcon } from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/icons';
import { PLAN_TEXT } from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/planPageRevampUi';
import { usePlanPageRevampVariant } from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/usePlanPageUiRevamp';
import { STATUS } from 'projectConstants';
import CalenderIcon from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/icons/CalenderIcon';

export interface Properties {
  planPermission: boolean;
  refetchRequest?: () => void;
  variant?: 'legacy' | 'revamp';
}

const TimeScaleFilter = ({ planPermission, variant = 'legacy' }: Properties) => {
  const { modelId } = useParams();
  const themeColors = useSelector(getPlanPageThemeColors);
  const isRevamp = usePlanPageRevampVariant(variant);
  const [timescale, setTimescale] = useState('Y');
  const [maxValue, setMaxValue] = useState(3);
  const [periodAmount, setPeriodAmount] = useState('3');
  const [endIn, setEndIn] = useState<Date | any>(new Date());
  const [isPermissionTimescaleModal, setIsPermissionTimescaleModal] = useState(false);
  const [startIn, setStartIn] = useState<Date | any>(new Date());
  const dispatch: any = useDispatch();
  const model = useSelector(getModelDetails);
  const modelStatus = useSelector(modelDetailStatus);
  const UserSubscriptionLimitData = useSelector(getUserSubscriptionLimits);
  const timeStatusError = useSelector(getTimeStatus);
  const dimension = useSelector(getDimension);
  const reference = useRef() as React.MutableRefObject<HTMLInputElement>;
  const [timeScalePopover, setTimeScalePopover] = useBoolean();
  const selectedGroupBy = useSelector(overallGroupByFilter);
  const indicatorFilter = useSelector(overallIndicatorFilter);
  const selectedScenario = useSelector(getBaseScenario);
  const selectedBlockDimension = useSelector(getSelectedBlockDimension);
  const [filterStartDate, setFilterStartDate] = useState<Date | any>();
  const [filterEndDate, setFilterEndDate] = useState<Date | any>();
  const formatTimeProperties = (properties: TimeProperties) => {
    return { ...properties, time_granularity: properties?.time_granularity ?? 'M' };
  };
  const compareScenario = useSelector(getCompareScenario);
  const properties = formatTimeProperties(model?.time_properties);
  const timeGranualityLimit =
    UserSubscriptionLimitData?.subscription_features_limit?.model_timescale;
  const parameters = useParams();
  useEffect(() => {
    if (timeGranualityLimit === null) {
      setMaxValue(-1);
    } else {
      if (timescale.toLowerCase() === 'm') {
        setMaxValue(timeGranualityLimit);
      }
      if (timescale.toLowerCase() === 'y') {
        setMaxValue(timeGranualityLimit / 12);
      }
    }
  }, [timescale, timeGranualityLimit]);

  const calculateShowData = (data: any) => {
    if (timescale === 'Y') {
      return data?.getFullYear();
    }
    return `${data?.toLocaleDateString('en-US', {
      month: 'short',
    })} ${data?.getFullYear().toString().slice(-2)}`;
  };

  const getExactDate = (dateString: string) => {
    if (timescale === 'Y') return dateString;
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    const dd = new Date(dateString);
    const month = months[dd.getMonth()]; // Get the month name from the array
    const year = dd.getFullYear().toString().slice(-2); // Get the last two digits of the year
    const newString = `${month}-${year}`;
    return newString;
  };
  const convertToDate = (input: string) => {
    const months: any = {
      Jan: 0,
      Feb: 1,
      Mar: 2,
      Apr: 3,
      May: 4,
      Jun: 5,
      Jul: 6,
      Aug: 7,
      Sep: 8,
      Oct: 9,
      Nov: 10,
      Dec: 11,
    };

    // Validate the input format
    const match = input.match(/^([A-Za-z]{3})-(\d{2})$/);
    if (!match) return null;

    const [, month, yearSuffix] = match;
    const monthIndex = months[month];
    if (monthIndex === undefined) return null;

    // Convert the year suffix to a full year
    const currentYear = new Date().getFullYear();
    const century = Math.floor(currentYear / 100) * 100;
    const year = century + Number.parseInt(yearSuffix, 10);

    // Create the date object and format it
    const date = new Date(year, monthIndex, 1);
    return date;
  };

  const isValidMonthYearFormat = (input: string): boolean => {
    // Regular expression to check "MMM-YY" format (e.g., "Jan-26", "Feb-24")
    const monthYearRegex = /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-\d{2}$/;

    // Check if input matches the pattern
    if (!monthYearRegex.test(input)) return false;

    // Extract year and convert to a full four-digit year
    const [, month, shortYear] =
      input.match(/(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-(\d{2})/) || [];
    if (!month || !shortYear) return false;

    // Convert short year to full year (assume 20XX for simplicity)
    const fullYear = Number.parseInt(`20${shortYear}`, 10);

    // Check if the parsed date is valid
    return moment(`${month} ${fullYear}`, 'MMM YYYY', true).isValid();
  };
  useEffect(() => {
    if (!properties || !modelId) return;

    const planFilters = localStorage.getItem('global-modal-filters');
    const data = planFilters ? JSON.parse(planFilters) : {};
    const modalFilters = data[modelId] || {};
    const object = modalFilters.timeFilter ? modalFilters.timeFilter || {} : {};

    if (
      object &&
      isValidMonthYearFormat(object?.startDatePlanPage) &&
      isValidMonthYearFormat(object?.endDatePlanPage)
    ) {
      const { startDatePlanPage, endDatePlanPage } = object;
      const newStart = convertToDate(startDatePlanPage);
      const newEnd = convertToDate(endDatePlanPage);

      // Only update if values are different
      if (
        !startIn ||
        startIn.getTime() !== newStart?.getTime() ||
        !endIn ||
        endIn.getTime() !== newEnd?.getTime()
      ) {
        setStartIn(newStart);
        setEndIn(newEnd);
        dispatch(setPlanPageDate({ endDatePlanPage, startDatePlanPage }));
      }
    } else {
      const timeGranularity = properties?.time_granularity ?? 'M';
      const value = Number.isNaN(properties?.plan_duration_periods)
        ? timeGranularity.toLowerCase() === 'm'
          ? 36
          : 3
        : Number(properties?.plan_duration_periods);

      // Only update if values are different
      if (timescale !== timeGranularity.toUpperCase()) setTimescale(timeGranularity.toUpperCase());
      if (periodAmount !== value.toString()) setPeriodAmount(value.toString());

      const newStart = properties?.starting_period ? new Date(properties?.starting_period) : null;
      if (!startIn || (newStart && startIn.getTime() !== newStart.getTime())) setStartIn(newStart);
      if (!filterStartDate || (filterStartDate && newStart && filterStartDate !== newStart))
        setFilterStartDate(newStart);

      if (newStart) {
        const endDate = new Date(newStart);
        const targetValue = timeGranularity.toLowerCase() === 'm' ? value : value * 12;
        const dateEnd = endDate.setMonth(endDate.getMonth() + targetValue);
        const newEnd = new Date(dateEnd);
        if (!endIn || endIn.getTime() !== newEnd.getTime()) setEndIn(newEnd);
        if (!filterEndDate || (filterEndDate && filterEndDate !== newEnd)) setFilterEndDate(newEnd);
      }
    }
  }, [modelId, properties]);

  const applyPlanPageFilter = (startDatePlanPage: string, endDatePlanPage: string) => {
    if (!compareScenario) {
      dispatch(
        fetchAnalysisSectionDataVersion2({
          blockId: Number(parameters?.blockId),
          parameters: {
            dim_id:
              selectedGroupBy === 'Total'
                ? null
                : dimension && !selectedGroupBy
                ? dimension?.id
                : selectedGroupBy
                ? Number.parseInt(selectedGroupBy, 10)
                : null,
            dim_sort: 'ASC',
            time_filter: `[{ 'start_date': '${startDatePlanPage}', 'end_date': '${endDatePlanPage}' }]`,
            time_aggregation: 'Y,Q,M',
            indicator_filter: indicatorFilter,
            scenario_id: selectedScenario?.id,
            dim_filter: JSON.stringify(selectedBlockDimension),
          },
        }),
      );
    } else {
      dispatch(
        fetchCompareAnalysisSectionData({
          blockId: Number(parameters?.blockId),
          parameters: {
            params: {
              dim_id:
                selectedGroupBy === 'Total'
                  ? null
                  : dimension && !selectedGroupBy
                  ? dimension?.id
                  : selectedGroupBy
                  ? Number.parseInt(selectedGroupBy, 10)
                  : null,
              dim_sort: 'ASC',
              time_filter: `[{ 'start_date': '${startDatePlanPage}', 'end_date': '${endDatePlanPage}' }]`,
              time_aggregation: 'Y,Q,M',
              indicator_filter: indicatorFilter,
              scenario_id: selectedScenario?.id,
              compare_scenario_id: compareScenario?.id,
              dim_filter: JSON.stringify(selectedBlockDimension),
            },
          },
        }),
      );
      dispatch(
        fetchCompareTableData({
          blockId: Number(parameters?.blockId),
          parameters: {
            params: {
              dim_id:
                selectedGroupBy === 'Total'
                  ? null
                  : dimension && !selectedGroupBy
                  ? dimension?.id
                  : selectedGroupBy
                  ? Number.parseInt(selectedGroupBy, 10)
                  : null,
              dim_sort: 'ASC',
              time_filter: `[{ 'start_date': '${startDatePlanPage}', 'end_date': '${endDatePlanPage}' }]`,
              time_aggregation: 'Y,Q,M',
              indicator_filter: indicatorFilter,
              scenario_id: selectedScenario?.id,
              compare_scenario_id: compareScenario?.id,
              dim_filter: JSON.stringify(selectedBlockDimension),
            },
          },
        }),
      );
    }

    // --- Synchronize modal filters for time in localStorage ---
    const planFilters = localStorage.getItem('global-modal-filters');
    const data = planFilters ? JSON.parse(planFilters) : {};
    if (modelId) {
      const modalFilters = data[modelId] || {};

      // Ensure timeFilter exists
      if (!modalFilters.timeFilter) {
        modalFilters.timeFilter = {};
      }

      // Set the time range for the blockId (modal)
      modalFilters.timeFilter = { startDatePlanPage, endDatePlanPage };

      // Save back to localStorage
      localStorage.setItem(
        'global-modal-filters',
        JSON.stringify({ ...data, [modelId]: modalFilters }),
      );
    }
  };

  const changeTimeInput = useCallback(
    (time: string, amount: string, startDate: Date | null, endDate?: Date | null) => {
      const requestObject: any = {
        model_id: Number(modelId),
        request_body: {
          time_granularity: time,
          plan_duration_periods: amount,
        },
      };
      if (endDate) {
        const endInDate = endDate.toDateString();
        requestObject.request_body.ending_period =
          time === 'Y' ? dayjs(endInDate).format('YYYY') : dayjs(endInDate).format('DD MMM YY');
      }
      if (startDate !== null) {
        const startInDate = startDate.toDateString();
        requestObject.request_body.starting_period =
          time === 'Y' ? dayjs(startInDate).format('YYYY') : dayjs(startInDate).format('DD MMM YY');
      }
      const endDatePlanPage = getExactDate(requestObject.request_body.ending_period);
      const startDatePlanPage = getExactDate(requestObject.request_body.starting_period);
      applyPlanPageFilter(startDatePlanPage, endDatePlanPage);
      dispatch(setPlanPageDate({ endDatePlanPage, startDatePlanPage }));
    },
    [modelId, filterStartDate, filterEndDate, compareScenario, selectedBlockDimension],
  );
  const formatFilterDate = () => {
    if (filterStartDate && filterEndDate) {
      const startDate = new Date(filterStartDate).toDateString();
      setFilterStartDate(
        timescale === 'Y' ? dayjs(startDate).format('YYYY') : dayjs(startDate).format('DD MMM YY'),
      );
      const endDate = new Date(filterEndDate).toDateString();
      setFilterEndDate(
        timescale === 'Y' ? dayjs(endDate).format('YYYY') : dayjs(endDate).format('DD MMM YY'),
      );
    }
  };

  useEffect(() => {
    formatFilterDate();
  }, [filterStartDate, filterEndDate]);
  useEffect(() => {
    if (properties) {
      const timeGranularity = properties?.time_granularity ?? 'M';
      setTimescale(timeGranularity.toUpperCase());
      const value = Number.isNaN(properties?.plan_duration_periods)
        ? timeGranularity.toLowerCase() === 'm'
          ? 36
          : 3
        : Number(properties?.plan_duration_periods);
      setPeriodAmount(value.toString());
      setStartIn(properties?.starting_period ? new Date(properties?.starting_period) : null);
      setFilterStartDate(
        properties?.starting_period ? new Date(properties?.starting_period) : null,
      );
    }
    if (properties && properties?.starting_period) {
      const endDate = new Date(properties?.starting_period);
      const timeGranularity = properties?.time_granularity ?? 'M';

      const value = Number.isNaN(properties?.plan_duration_periods)
        ? timeGranularity.toLowerCase() === 'm'
          ? 36
          : 3
        : Number(properties?.plan_duration_periods);
      const targetValue = timeGranularity.toLowerCase() === 'm' ? value : value * 12;
      const dateEnd = endDate.setMonth(endDate.getMonth() + targetValue);
      setEndIn(new Date(dateEnd));
      setFilterEndDate(new Date(dateEnd));
    } else {
      setEndIn(null);
    }

    return () => {
      dispatch(resetTimeStatus());
    };
  }, [
    modelStatus === STATUS.SUCCESS,
    isPermissionTimescaleModal === true,
    timeStatusError === true,
  ]);

  const onChangePeriod = async (valueDiff: any) => {
    const newValue = valueDiff;
    if (Number(newValue) <= maxValue) {
      setPeriodAmount(newValue);
    } else if (maxValue === -1) {
      setPeriodAmount(newValue);
    } else {
      setIsPermissionTimescaleModal(true);
      setTimeout(() => {
        const timeGranularity = properties?.time_granularity ?? 'M';
        const value = Number.isNaN(properties?.plan_duration_periods)
          ? timeGranularity.toLowerCase() === 'm'
            ? 36
            : 3
          : Number(properties?.plan_duration_periods);
        setPeriodAmount(value.toString());
      }, 1000);
      setTimeout(() => {
        setIsPermissionTimescaleModal(false);
      }, 5000);
    }
  };

  const startDateChange = (startDate: Date) => {
    if (timescale.toLowerCase() === 'm') {
      const monthsApart =
        (endIn.getFullYear() - startDate.getFullYear()) * 12 +
        (endIn.getMonth() - startDate.getMonth());
      setPeriodAmount(`${monthsApart}`);
      onChangePeriod(monthsApart);
      return `${monthsApart}`;
    }
    const yearDifference = endIn.getFullYear() - startDate.getFullYear();
    setPeriodAmount(yearDifference.toString());
    onChangePeriod(`${yearDifference}`);
    return `${yearDifference}`;
  };

  const endDateChange = (endDate: Date) => {
    if (timescale.toLowerCase() === 'm') {
      const monthsApart =
        (endDate.getFullYear() - startIn.getFullYear()) * 12 +
        (endDate.getMonth() - startIn.getMonth());
      setPeriodAmount(`${monthsApart}`);
      onChangePeriod(monthsApart);
      return `${monthsApart}`;
    }
    const yearDifference = endDate.getFullYear() - startIn.getFullYear();
    setPeriodAmount(yearDifference.toString());
    onChangePeriod(`${yearDifference}`);
    return `${yearDifference}`;
  };

  const onSelectStartDate = async (date: Date) => {
    setStartIn(date);
    const diff = startDateChange(date);
    onChangePeriod(Number(diff));
    changeTimeInput(timescale, diff, date, endIn);
  };

  const onSelectEndDate = useCallback(
    (date: Date) => {
      setEndIn(date);
      const diff = endDateChange(date);
      onChangePeriod(Number(diff));
      changeTimeInput(timescale, diff, startIn, date);
    },
    [changeTimeInput, periodAmount, timescale],
  );

  const handleClearSelection = () => {
    setStartIn(null);
  };

  useOutsideClick({
    ref: reference,
    handler: () => {
      setTimeScalePopover.off();
    },
  });
  const ChevronIcon: React.ReactNode = useMemo(
    () =>
      isRevamp ? (
        <PlanToolbarChevronDownIcon width={10} height={6} color={themeColors.primary} />
      ) : (
        <ChevronDownIcon boxSize={5} />
      ),
    [isRevamp, themeColors.primary],
  );

  const CalendarIcon: React.ReactNode = useMemo(
    () =>
      isRevamp ? (
        <CalenderIcon width={14} height={14} color={themeColors.primary} />
      ) : (
        <FiCalendar size={16} color={themeColors.primary} />
      ),
    [isRevamp, themeColors.primary],
  );

  return (
    <Flex ps={isRevamp ? 0 : 0} gap={isRevamp ? 0 : 1} mr={isRevamp ? 0 : 2}>
      <Popover
        placement='bottom-start'
        closeOnBlur={false}
        isOpen={timeScalePopover}
        onOpen={setTimeScalePopover.on}
        onClose={setTimeScalePopover.off}
        isLazy
        lazyBehavior='keepMounted'
      >
        <Box ref={reference} h={isRevamp ? '40px' : undefined}>
          <PopoverTrigger>
            {isRevamp ? (
              <Flex
                cursor='pointer'
                alignItems='center'
                gap={3}
                px={3}
                h='100%'
                minH='40px'
                bg={themeColors.activeBg}
                _hover={{ bg: themeColors.activeBg }}
                _focus={{ outline: 'none', boxShadow: 'none' }}
              >
                {CalendarIcon}
                <Text {...PLAN_TEXT.bodyMedium} color={themeColors.textDark} whiteSpace='nowrap'>
                  {calculateShowData(startIn)} - {calculateShowData(endIn)}
                </Text>
                {ChevronIcon}
              </Flex>
            ) : (
              <Flex cursor={'pointer'} alignItems={'center'}>
                <Calendar
                  style={{
                    marginRight: '.2rem',
                  }}
                  width={8}
                  height={6}
                  color='#69B4B8'
                />
                {ChevronIcon}
                <Text ml={1} size={'Roboto-12'}>
                  {calculateShowData(startIn)}
                </Text>
                <Text fontSize={'14px'} mx={1}>
                  -
                </Text>
                <Text size={'Roboto-12'}>{calculateShowData(endIn)}</Text>
              </Flex>
            )}
          </PopoverTrigger>
          <PopoverContent
            filter={isRevamp ? 'none' : 'drop-shadow(0px 0px 8px rgba(0, 0, 0, 0.25))'}
            bg={isRevamp ? themeColors.surface : 'white'}
            border={isRevamp ? '1px solid' : 'none'}
            borderColor={isRevamp ? themeColors.border : undefined}
            mt={isRevamp ? 2 : 3}
            borderRadius={isRevamp ? '12px' : '8px'}
            boxShadow={isRevamp ? '0px 4px 16px rgba(0, 0, 0, 0.1)' : undefined}
          >
            <PopoverBody ps={isRevamp ? 5 : 4} py={isRevamp ? 4 : undefined}>
              <Box pt={isRevamp ? 0 : 4} pb={2}>
                <Text
                  color={isRevamp ? themeColors.textDark : '#6562CF'}
                  fontSize={isRevamp ? '14px' : 'xs'}
                  fontWeight='600'
                >
                  Timescale
                </Text>
              </Box>
              <Flex justify={'start'} align={'center'} pb={3}>
                <DatePickerInputButton
                  clearSection={false}
                  clearDateInput={handleClearSelection}
                  selected={startIn}
                  onChange={onSelectStartDate}
                  timescale={timescale}
                  isDisabled={planPermission}
                  minDate={new Date(filterStartDate)}
                  maxDate={new Date(endIn)}
                />
                <Flex px={2}>
                  <BsArrowRight color={'black'} style={{ width: '20px', height: '20px' }} />
                </Flex>
                <DatePickerInputButton
                  clearSection={false}
                  clearDateInput={handleClearSelection}
                  selected={endIn}
                  onChange={onSelectEndDate}
                  timescale={timescale}
                  isDisabled={planPermission}
                  minDate={new Date(startIn)}
                  maxDate={new Date(filterEndDate)}
                />
              </Flex>
              <Text
                color={isRevamp ? themeColors.textSecondary : '#8B8AAB'}
                fontSize='12px'
                ps={isRevamp ? 0 : 1}
                pt={1}
              >
                {timescale === 'M'
                  ? `${Number(periodAmount) + 1} months`
                  : `${Number(periodAmount) + 1} years`}
              </Text>
              {isPermissionTimescaleModal && (
                <TimeScalePermission maxValue={maxValue} timescale={timescale} />
              )}
            </PopoverBody>
          </PopoverContent>
        </Box>
      </Popover>
    </Flex>
  );
};

export default PlanPermission(TimeScaleFilter);
